import Icon from "@components/Icon";
import { colours, getColours, richTextHelper } from "@helpers";
import type { IAnnouncementModuleFields } from "@types-contentful";
import React from "react";
import styled from "styled-components";
import CTAButton from "./CTAButton";

const Container = styled.div<{
	resetModuleMarginBottom: string;
	backgroundColour: string;
	hideOnDesktop: boolean | undefined;
}>`
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};
  background-color: ${(props) => getColours(props.backgroundColour)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px 35px;

  @media (min-width: 834px) {
    display: ${({ hideOnDesktop }) => (hideOnDesktop ? "none" : "flex")};
    flex-direction: row;
  }
`;

const Span = styled.span`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const Copy = styled.span<{
	NoMarginBottomWhenNoCTA: boolean;
	copyColourForDarkBackgroundColour: string;
}>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 3px;
  margin-bottom: ${({ NoMarginBottomWhenNoCTA }) =>
		NoMarginBottomWhenNoCTA ? "0px" : "8px"};
  p,
  a,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    text-align: center;
    ${({ copyColourForDarkBackgroundColour }) =>
			copyColourForDarkBackgroundColour === "Midnight Blue"
				? `color: ${colours.WHITE}`
				: `color: ${colours.MIDNIGHT_BLUE}`};
  }
  @media (min-width: 834px) {
    margin-right: 16px;
    margin-bottom: 0;
  }
`;

const AnnouncementModule = ({
	name,
	removeNegativeSpacingBelowModule,
	copy,
	backgroundColour,
	ctaButtonLabel,
	ctaButtonUrl,
	ctaButtonColour,
	hideOnDesktop,
	iconRef,
}: IAnnouncementModuleFields) => {
	const resetModuleMarginBottom = removeNegativeSpacingBelowModule;
	const noCTA = !ctaButtonUrl;
	const copyColourForDarkBackgroundColour = backgroundColour;

	const renderIcon = () => {
		if (iconRef) {
			return (
				<Icon
					iconName={iconRef}
					iconSizes={{
						iconMobile: 30,
						iconTablet: 30,
						iconLaptop: 30,
						iconDesktop: 30,
						iconDesktopLarge: 30,
					}}
					alt={iconRef}
				/>
			);
		}
		return null;
	};

	return (
		<Container
			className={hideOnDesktop ? "mobile" : "desktop"}
			resetModuleMarginBottom={resetModuleMarginBottom}
			backgroundColour={backgroundColour}
			hideOnDesktop={hideOnDesktop}
			data-testid={`announcementModule-${name?.toLowerCase().replace(/\s+/g, "-")}`}
		>
			{copy && (
				<Span>
					{renderIcon()}
					<Copy
						copyColourForDarkBackgroundColour={
							copyColourForDarkBackgroundColour
						}
						NoMarginBottomWhenNoCTA={noCTA}
					>
						{richTextHelper(copy)}
					</Copy>
				</Span>
			)}
			{ctaButtonUrl && ctaButtonColour && name && ctaButtonLabel && (
				<CTAButton
					url={ctaButtonUrl}
					ctaButtonColour={ctaButtonColour}
					gtmCtaPosition={name}
					gtmCtaText={ctaButtonLabel}
				>
					{ctaButtonLabel}
				</CTAButton>
			)}
		</Container>
	);
};

export default AnnouncementModule;
